// src/screens/GestionUsuariosScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, writeBatch, getDocs, getDoc, doc, deleteDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoPersonCircleOutline, IoHomeOutline, IoKeyOutline, IoDownloadOutline, IoWarning, IoSyncOutline, IoPeopleOutline, IoTrash, IoPencil, IoChevronBack, IoChevronForward, IoEyeOutline } from 'react-icons/io5';
import EditarUsuarioModal from '../components/Admin/EditarUsuarioModal';
import './GestionUsuariosScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import AvatarViewer from '../components/Shared/AvatarViewer';

// --- Badges ---
const ResidenteTipoBadge = ({ tipo }) => {
  if (tipo === 'Propietario') {
    return (
      <span className="status-pill propietario">
        <IoKeyOutline size={14} /> Propietario
      </span>
    );
  }
  return (
    <span className="status-pill inquilino">
      <IoHomeOutline size={14} /> Inquilino
    </span>
  );
};

const EstatusMorosoBadge = ({ residente }) => {
  if (residente.estatusValidacion === 'pendiente') {
    return (
      <span className="status-pill status-warning" style={{ backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #ffedd5' }}>
        <IoWarning size={14} style={{ marginRight: '4px' }} /> Pendiente
      </span>
    );
  }
  if (residente.hasConvenio) {
    return (
      <span className="status-pill status-convenio">
        Convenio
      </span>
    );
  }
  if (residente.isMoroso) {
    return (
      <span className="status-pill status-moroso">
        <IoWarning size={14} /> Moroso
      </span>
    );
  }
  return (
    <span className="status-pill status-aldia">
      Al Corriente
    </span>
  );
};

function GestionUsuariosScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [residentes, setResidentes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Custom Modal Dialog States
  const [confirmModal, setConfirmModal] = useState({
      show: false,
      title: '',
      message: '',
      onConfirm: null
  });
  const [alertModal, setAlertModal] = useState({
      show: false,
      title: '',
      message: ''
  });

  const triggerConfirm = (title, message, onConfirm) => {
      setConfirmModal({ show: true, title, message, onConfirm });
  };

  const triggerAlert = (title, message) => {
      setAlertModal({ show: true, title, message });
  };

  useEffect(() => {
    if (!userData?.condominioId) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "usuarios"),
      where("condominioId", "==", userData.condominioId),
      orderBy("nombre", "asc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const listaResidentes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setResidentes(listaResidentes);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar residentes: ", error);
      if (error.code === 'failed-precondition') {
        triggerAlert("Error de Firestore", "Falta índice en Firestore. Revisa la consola.");
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [userData]);

  // --- AUTOMATIC BACKGROUND SYNC ON MOUNT ---
  useEffect(() => {
    if (!userData?.condominioId || residentes.length === 0) return;

    const runSilentSync = async () => {
      try {
        const qCargos = query(
          collection(db, "cargos"),
          where("condominioId", "==", userData.condominioId),
          where("estatus", "in", ["pendiente", "parcial", "vencido"])
        );
        const snapshotCargos = await getDocs(qCargos);

        // Fetch active convenios
        const qConvenios = query(
          collection(db, "convenios"),
          where("condominioId", "==", userData.condominioId),
          where("estatus", "==", "activo")
        );
        const snapshotConvenios = await getDocs(qConvenios);

        // Get condo config
        const condoSnap = await getDoc(doc(db, "condominios", userData.condominioId));
        const activeAccess = condoSnap.exists() ? (condoSnap.data().conveniosConfig?.activeAccess || 'completo') : 'completo';

        const residentsWithActiveConvenio = new Set();
        snapshotConvenios.docs.forEach(docSnap => {
          const data = docSnap.data();
          if (data.residenteId) {
            residentsWithActiveConvenio.add(data.residenteId);
          }
        });

        const morososIds = new Set();
        const unidadesMorosas = new Set();
        const ahora = Timestamp.now().toMillis();
        
        snapshotCargos.docs.forEach(docSnap => {
          const data = docSnap.data();
          const deuda = data.monto - (data.montoPagado || 0);
          const vencimiento = data.fechaVencimiento?.toMillis ? data.fechaVencimiento.toMillis() : new Date(data.fechaVencimiento).getTime();
          const estaVencido = vencimiento < ahora;
          if (deuda > 1.0 && estaVencido) {
            if (data.residenteId) morososIds.add(data.residenteId);
            if (data.unidadResidente && data.unidadResidente.trim() !== '' && data.unidadResidente !== 'N/A') {
              unidadesMorosas.add(data.unidadResidente.trim());
            }
          }
        });

        const batch = writeBatch(db);
        let updatesCount = 0;

        residentes.forEach(res => {
          const belongsToMorososUnit = Boolean(res.numeroCasa && res.numeroCasa.trim() !== '' && res.numeroCasa !== 'N/A' && unidadesMorosas.has(res.numeroCasa.trim()));
          let shouldBeMoroso = Boolean(morososIds.has(res.id) || belongsToMorososUnit);
          const hasActiveConvenio = residentsWithActiveConvenio.has(res.id);

          // Apply active convenios rule
          if (shouldBeMoroso && hasActiveConvenio) {
            if (activeAccess === 'completo') {
              shouldBeMoroso = false;
            }
          }
          
          if (Boolean(res.isMoroso) !== shouldBeMoroso || Boolean(res.hasConvenio) !== hasActiveConvenio) {
            const ref = doc(db, "usuarios", res.id);
            batch.update(ref, { 
              isMoroso: shouldBeMoroso,
              hasConvenio: hasActiveConvenio
            });
            updatesCount++;
          }
        });

        if (updatesCount > 0) {
          console.log(`[Auto-Sync] Committing updates for ${updatesCount} residents.`);
          await batch.commit();
        }
      } catch (e) {
        console.error("Error in silent status sync:", e);
      }
    };

    runSilentSync();
  }, [userData, residentes.length]);

  // --- SYNC STATUS LOGIC ---
  const handleSyncStatus = async () => {
    triggerConfirm(
      "Sincronizar Estatus",
      "¿Recalcular estatus 'Moroso' para TODOS los residentes basado en sus deudas actuales?",
      async () => {
        setLoading(true);
        try {
          // 1. Get all pending charges
          const qCargos = query(
            collection(db, "cargos"),
            where("condominioId", "==", userData.condominioId),
            where("estatus", "in", ["pendiente", "parcial", "vencido"])
          );
          const snapshotCargos = await getDocs(qCargos);

          // Get active convenios
          const qConvenios = query(
            collection(db, "convenios"),
            where("condominioId", "==", userData.condominioId),
            where("estatus", "==", "activo")
          );
          const snapshotConvenios = await getDocs(qConvenios);

          // Get condo config
          const condoSnap = await getDoc(doc(db, "condominios", userData.condominioId));
          const activeAccess = condoSnap.exists() ? (condoSnap.data().conveniosConfig?.activeAccess || 'completo') : 'completo';

          const residentsWithActiveConvenio = new Set();
          snapshotConvenios.docs.forEach(doc => {
            const data = doc.data();
            if (data.residenteId) {
              residentsWithActiveConvenio.add(data.residenteId);
            }
          });

          // 2. Map Debtors (only those with overdue charges)
          const morososIds = new Set();
          const unidadesMorosas = new Set();
          const ahora = Timestamp.now().toMillis();
          
          snapshotCargos.docs.forEach(doc => {
            const data = doc.data();
            const deuda = data.monto - (data.montoPagado || 0);
            const vencimiento = data.fechaVencimiento?.toMillis ? data.fechaVencimiento.toMillis() : new Date(data.fechaVencimiento).getTime();
            const estaVencido = vencimiento < ahora;
            if (deuda > 1.0 && estaVencido) { // tolerance
              if (data.residenteId) morososIds.add(data.residenteId);
              if (data.unidadResidente && data.unidadResidente.trim() !== '' && data.unidadResidente !== 'N/A') {
                unidadesMorosas.add(data.unidadResidente.trim());
              }
            }
          });

          // 3. Batch Update Users
          const batch = writeBatch(db);
          let updatesCount = 0;

          residentes.forEach(res => {
            // Evaluate if this specific user has a direct debt, OR if their unit has a debt.
            const belongsToMorososUnit = Boolean(res.numeroCasa && res.numeroCasa.trim() !== '' && res.numeroCasa !== 'N/A' && unidadesMorosas.has(res.numeroCasa.trim()));
            let shouldBeMoroso = Boolean(morososIds.has(res.id) || belongsToMorososUnit);
            const hasActiveConvenio = residentsWithActiveConvenio.has(res.id);

            // Apply active convenios rules
            if (shouldBeMoroso && hasActiveConvenio) {
              if (activeAccess === 'completo') {
                shouldBeMoroso = false;
              }
            }
            
            if (Boolean(res.isMoroso) !== shouldBeMoroso || Boolean(res.hasConvenio) !== hasActiveConvenio) {
              const ref = doc(db, "usuarios", res.id);
              batch.update(ref, { 
                isMoroso: shouldBeMoroso,
                hasConvenio: hasActiveConvenio
              });
              updatesCount++;
            }
          });

          if (updatesCount > 0) {
            await batch.commit();
            triggerAlert("Sincronización Completada", `Se actualizaron ${updatesCount} residentes.`);
          } else {
            triggerAlert("Sincronización", "Todos los estatus ya estaban correctos.");
          }

        } catch (e) {
          console.error(e);
          triggerAlert("Error de Sincronización", "Error al sincronizar: " + e.message);
        } finally {
          setLoading(false);
        }
      }
    );
  };

  // --- DELETE USER ---
  const handleDeleteUser = async (user) => {
    triggerConfirm(
      "Eliminar Usuario",
      `¿Estás seguro de que deseas eliminar permanentemente a ${user.nombre}? Esta acción NO se puede deshacer y borrará el registro del condominio.`,
      async () => {
        try {
          // 1. Delete associated cargos (charges) and their ledger entries (contabilidad_asientos)
          const qCargos = query(collection(db, "cargos"), where("residenteId", "==", user.id));
          const cargosSnap = await getDocs(qCargos);
          
          for (const cargoDoc of cargosSnap.docs) {
              // Delete linked ledger entries (asientos)
              const qLedger = query(collection(db, "contabilidad_asientos"), where("referenciaId", "==", cargoDoc.id));
              const ledgerSnap = await getDocs(qLedger);
              for (const ledgerDoc of ledgerSnap.docs) {
                  await deleteDoc(ledgerDoc.ref);
              }
              // Delete cargo
              await deleteDoc(cargoDoc.ref);
          }

          // 2. Delete user doc
          await deleteDoc(doc(db, "usuarios", user.id));
          triggerAlert("Usuario Eliminado", "El usuario y todos sus cargos asociados han sido eliminados correctamente.");
        } catch (e) {
          console.error("Error deleting user and cargos:", e);
          triggerAlert("Error al Eliminar", "Error al eliminar: " + e.message);
        }
      }
    );
  };

  // --- EXPORT CSV ---
  const handleExportCSV = () => {
    const csvHeaders = "Nombre,Email,Unidad/Casa,Tipo Residente,Celular,Estatus,UID\n";
    const csvRows = residentes.map(r => {
      const nombre = `"${r.nombre || ''}"`;
      const email = `"${r.email || ''}"`;
      const unidad = `"${r.numeroCasa || 'No especificada'}"`;
      const tipo = `"${r.tipoResidente || 'Inquilino'}"`;
      const celular = `"${r.numeroCelular || ''}"`;
      const estatus = r.isMoroso ? "Moroso" : "Al Corriente";
      const uid = `"${r.uid}"`;
      return [nombre, email, unidad, tipo, celular, estatus, uid].join(',');
    }).join('\n');

    const csvContent = csvHeaders + csvRows;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `directorio_residentes_${userData.condominioId}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // --- PAGINATION LOGIC ---
  const totalPages = itemsPerPage === 'Todos' ? 1 : Math.ceil(residentes.length / itemsPerPage);
  const paginatedResidentes = itemsPerPage === 'Todos'
    ? residentes
    : residentes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-usuarios" />

      <main className="ks-main">
        <KondoBrandHeader title="Administración" showBackButton={false} />

        <div className="ks-scroll-area">
          <div className="usuarios-container">
            {/* --- Premium Banner --- */}
            <div className="muro-header-stitch">
              <div className="muro-hero-card">
                <div className="muro-hero-content">
                  <h1>Directorio</h1>
                  <p>Gestiona la información de contacto y estatus de los residentes.</p>
                  
                  <div className="muro-hero-actions-row">
                    <button
                      className="muro-hero-action-btn"
                      onClick={handleSyncStatus}
                      disabled={loading}
                    >
                      <IoSyncOutline size={18} />
                      <span>Sincronizar</span>
                    </button>
                    {!loading && residentes.length > 0 && (
                      <button
                        className="muro-hero-action-btn secondary"
                        onClick={handleExportCSV}
                      >
                        <IoDownloadOutline size={18} />
                        <span>Exportar</span>
                      </button>
                    )}
                  </div>
                </div>
                <IoPeopleOutline className="muro-hero-icon-decor" />
              </div>
            </div>

            {/* --- Pagination Controls (Top) --- */}
            {!loading && residentes.length > 0 && (
              <div className="pagination-controls-top">
                <div className="items-per-page">
                  <label>Mostrar: </label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(e.target.value === 'Todos' ? 'Todos' : Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value="Todos">Todos</option>
                  </select>
                  <span style={{ marginLeft: '10px', fontSize: '0.9rem', color: '#6b7280' }}>
                    Total: {residentes.length}
                  </span>
                </div>

                {itemsPerPage !== 'Todos' && totalPages > 1 && (
                  <div className="pagination-arrows">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                      className="page-btn-icon"
                    >
                      <IoChevronBack size={18} />
                    </button>
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                      className="page-btn-icon"
                    >
                      <IoChevronForward size={18} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* --- Main Card --- */}
            <div className="usuarios-card">
              {loading && <div className="empty-state"><p>Cargando residentes...</p></div>}

              {!loading && residentes.length === 0 && (
                <div className="empty-state">
                  <IoPeopleOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
                  <p>No hay residentes registrados en este condominio.</p>
                </div>
              )}

              {/* --- Modern Table --- */}
              {/* --- Modern Table (Desktop Only) --- */}
              {!loading && residentes.length > 0 && (
                <>
                  <div className="table-responsive hide-on-mobile">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th className="col-identidad">Residentes</th>
                          <th className="col-unidad">Unidad <span className="hide-on-mobile">(Casa/Depto)</span></th>
                          <th className="col-tipo">Tipo</th>
                          <th className="col-estatus">Estatus</th>
                          <th className="col-contacto">Contacto</th>
                          <th className="col-acciones">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedResidentes.map((residente) => (
                          <tr key={residente.id} className={residente.isMoroso ? 'row-moroso' : 'row-aldia'}>
                            <td className="col-identidad">
                              <div className="residente-flex-wrapper">
                                <div className="user-avatar" style={{ width: '40px', height: '40px', overflow: 'hidden', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <AvatarViewer avatarUrl={residente.fotoPerfilUrl} size={18} />
                                </div>
                                <div className="residente-text-info">
                                  <div className="residente-nombre">{residente.nombre}</div>
                                  <div className="residente-email">{residente.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="col-unidad">
                              <span style={{ fontWeight: '600', color: '#111827' }}>
                                {residente.numeroCasa || 'No especificada'}
                              </span>
                            </td>
                            <td className="col-tipo">
                              <ResidenteTipoBadge tipo={residente.tipoResidente} />
                            </td>
                            <td className="col-estatus">
                              <EstatusMorosoBadge residente={residente} />
                            </td>
                            <td className="col-contacto">{residente.numeroCelular || 'N/A'}</td>
                            <td className="actions-cell">
                              <div className="actions-grid">
                                {residente.estatusValidacion === 'pendiente' ? (
                                  <button
                                    className="action-btn-primary action-btn-full"
                                    onClick={() => setEditingUser(residente)}
                                    style={{ padding: '6px 12px', fontSize: '0.85em', gap: '6px', backgroundColor: '#10b981', borderColor: '#10b981' }}
                                    title="Validar Registro"
                                  >
                                    <span className="hide-on-mobile">Validar Registro</span>
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      className="action-btn-secondary action-btn-full"
                                      onClick={() => navigate(`/admin-usuario/${residente.id}`)}
                                      style={{ padding: '6px 12px', fontSize: '0.85em', gap: '6px' }}
                                      title="Ver Detalle"
                                    >
                                      <IoEyeOutline size={18} />
                                      <span className="hide-on-mobile">Ver Detalle</span>
                                    </button>
                                    <div className="actions-row">
                                      <button
                                        className="action-btn-secondary"
                                        onClick={() => setEditingUser(residente)}
                                        title="Editar Usuario"
                                      >
                                        <IoPencil />
                                      </button>
                                      <button
                                        className="action-btn-secondary danger-hover"
                                        onClick={() => handleDeleteUser(residente)}
                                        style={{ color: '#dc3545', background: '#fff0f1', border: '1px solid #fecaca' }}
                                        title="Eliminar Usuario"
                                      >
                                        <IoTrash />
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* --- Mobile Card List (Mobile Only) --- */}
                  <div className="mobile-residents-list show-on-mobile">
                    {paginatedResidentes.map((residente) => (
                      <div key={residente.id} className="mobile-resident-card" style={{ borderLeft: residente.isMoroso ? '4px solid #dc2626' : residente.estatusValidacion === 'pendiente' ? '4px solid #f59e0b' : '4px solid #16a34a' }}>
                        <div className="mobile-card-header">
                          <div className="mobile-card-profile">
                            <div className="mobile-card-avatar" style={{ width: '40px', height: '40px', overflow: 'hidden', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <AvatarViewer avatarUrl={residente.fotoPerfilUrl} size={18} />
                            </div>
                            <div className="mobile-card-info">
                              <span className="mobile-card-name">{residente.nombre}</span>
                              <span className="mobile-card-email">{residente.email}</span>
                            </div>
                          </div>
                          <div className="mobile-card-unit">
                            {residente.numeroCasa || 'N/A'}
                          </div>
                        </div>
                        
                        <div className="mobile-card-badges">
                          <ResidenteTipoBadge tipo={residente.tipoResidente} />
                          <EstatusMorosoBadge residente={residente} />
                        </div>

                        <div className="mobile-card-actions">
                          <div className="mobile-card-phone">
                            <span style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase' }}>Tel:</span> {residente.numeroCelular || 'N/A'}
                          </div>
                          
                          <div className="mobile-card-buttons">
                            {residente.estatusValidacion === 'pendiente' ? (
                              <button
                                className="action-btn-primary"
                                onClick={() => setEditingUser(residente)}
                                style={{ padding: '6px 12px', fontSize: '0.8rem', backgroundColor: '#10b981', borderColor: '#10b981', color: 'white', borderRadius: '8px' }}
                                title="Validar Registro"
                              >
                                Validar
                              </button>
                            ) : (
                              <>
                                <button
                                  className="action-btn-secondary"
                                  onClick={() => navigate(`/admin-usuario/${residente.id}`)}
                                  style={{ padding: '6px 10px' }}
                                  title="Ver Detalle"
                                >
                                  <IoEyeOutline size={16} />
                                </button>
                                <button
                                  className="action-btn-secondary"
                                  onClick={() => setEditingUser(residente)}
                                  style={{ padding: '6px 10px' }}
                                  title="Editar Usuario"
                                >
                                  <IoPencil size={14} />
                                </button>
                                <button
                                  className="action-btn-secondary danger-hover"
                                  onClick={() => handleDeleteUser(residente)}
                                  style={{ color: '#dc3545', background: '#fff0f1', border: '1px solid #fecaca', padding: '6px 10px' }}
                                  title="Eliminar Usuario"
                                >
                                  <IoTrash size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* --- Pagination Controls (Bottom) --- */}
              {!loading && itemsPerPage !== 'Todos' && totalPages > 1 && (
                <div className="pagination-footer">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="page-btn"
                  >
                    Anterior
                  </button>
                  <span className="page-info">Página {currentPage} de {totalPages}</span>
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="page-btn"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* --- EDIT MODAL --- */}
      {
        editingUser && (
          <EditarUsuarioModal
            user={editingUser}
            onClose={() => setEditingUser(null)}
            onSuccess={() => {
              setEditingUser(null);
            }}
          />
        )
      }

      <KondoBottomNav activeRoute="/gestion-usuarios" />

      {/* Custom Confirmation Modal */}
      {confirmModal.show && (
        <div className="confirm-modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-content confirm-modal-content premium-popup">
            <div className="confirm-icon-wrapper warning-icon-wrapper" style={{ backgroundColor: '#fee2e2', color: '#ef4444' }}>
              <IoWarning size={46} className="confirm-icon" />
            </div>
            <h3>{confirmModal.title}</h3>
            <p className="confirm-message">{confirmModal.message}</p>
            <div className="confirm-actions">
              <button 
                type="button" 
                className="btn-cancelar" 
                onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}
              >
                Cancelar
              </button>
              <button 
                type="button" 
                className="btn-confirmar-entrega" 
                onClick={() => {
                  if (confirmModal.onConfirm) confirmModal.onConfirm();
                  setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
                }}
                style={{ background: 'linear-gradient(135deg, #7B61FF, #6348D1)', boxShadow: '0 4px 12px rgba(123, 97, 255, 0.25)' }}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Alert Modal */}
      {alertModal.show && (
        <div className="confirm-modal-overlay" style={{ zIndex: 9999 }}>
          <div className="modal-content confirm-modal-content premium-popup">
            <div className="confirm-icon-wrapper success-icon-wrapper" style={{ backgroundColor: '#ecfdf5', color: '#10b981' }}>
              <IoWarning size={46} className="confirm-icon" style={{ color: '#10b981' }} />
            </div>
            <h3>{alertModal.title}</h3>
            <p className="confirm-message">{alertModal.message}</p>
            <div className="confirm-actions">
              <button 
                type="button" 
                className="btn-confirmar-entrega" 
                onClick={() => setAlertModal({ show: false, title: '', message: '' })}
                style={{ width: '100%', background: 'linear-gradient(135deg, #10b981, #059669)', flex: 'none', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)' }}
              >
                Aceptar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionUsuariosScreen;